import { createId } from '@paralleldrive/cuid2'
import type { HarmonyPackageBoardWorkitem } from '@rhythmic/db/types'
import { KanbanBoardWorkitemInsertSchema, KanbanContextSchema, KanbanExistingHarmonyItemSchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import type { HarmonyItem } from '~/src/queries'

import { getBoardItemsCollection } from '../getCollections'

export default function handleAddExistingItem(
  payload: HarmonyItem[],
  context:
    | {
        boardId: string
        packageId: string
        workspaceId: string
        teamId: string
      }
    | undefined,
): Transaction<HarmonyItem> {
  const validatedContext = KanbanContextSchema.parse(context)

  const boardItems = payload.map((item): HarmonyPackageBoardWorkitem => {
    const existingItem = KanbanExistingHarmonyItemSchema.parse(item)

    return KanbanBoardWorkitemInsertSchema.parse({
      id: `bwi_${createId()}`,
      board_id: validatedContext.boardId,
      column_id: existingItem.column_id,
      workitem_id: existingItem.hid,
      position: undefined,
      workspace_id: validatedContext.workspaceId,
    })
  })

  const collection = getBoardItemsCollection(validatedContext.boardId)

  const action = createOptimisticAction<{
    boardItems: HarmonyPackageBoardWorkitem[]
  }>({
    onMutate: ({ boardItems }) => {
      for (const boardItem of boardItems) collection.insert(boardItem)
    },
    mutationFn: async ({ boardItems }) => {
      const waitables = []
      for (const boardItem of boardItems) {
        const result = await $fetch<{ txid?: string }>('/api/board-workitems', {
          method: 'POST',
          body: {
            id: boardItem.id,
            board_id: boardItem.board_id,
            column_id: boardItem.column_id,
            workitem_id: boardItem.workitem_id,
            position: boardItem.position ?? null,
            workspace_id: boardItem.workspace_id ?? null,
          },
        })

        if (result?.txid && collection.utils?.awaitTxId) {
          waitables.push(collection.utils.awaitTxId(Number.parseInt(result.txid), 300000))
        }
      }

      if (waitables.length === 0) return { txid: undefined }

      await Promise.all(waitables)
      return { txid: undefined }
    },
  })

  return action({ boardItems })
}
